import React, { useEffect, useMemo, useState } from 'react';
import { getAuditLog, getUsers, getProspects, getDossiers } from '../../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SearchableSelect } from '../SearchableSelect';
import { Eye, RefreshCcw, Trash2, FilePlus, Edit3, X } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../ui/button';

const ICON_MAP = {
    create: FilePlus,
    update: Edit3,
    delete: Trash2,
};

const COLOR_MAP = {
    create: 'text-green-600 bg-green-50',
    update: 'text-blue-600 bg-blue-50',
    delete: 'text-red-600 bg-red-50',
};

const ENTITY_LABELS = {
    prospect: 'Client / Prospect',
    dossier: 'Projet / Dossier',
    invoice: 'Facture',
    cost: 'Coût',
    document: 'Document',
    relance: 'Relance',
    litige: 'Litige',
    reserve: 'Réserve',
    pv_reception: 'PV Réception',
    user: 'Utilisateur',
    settings: 'Paramètres',
};

const ENTITY_OPTIONS = [
    { value: 'all', label: 'Toutes les entités' },
    { value: 'prospect', label: 'Clients / Prospects' },
    { value: 'dossier', label: 'Projets / Dossiers' },
    { value: 'invoice', label: 'Factures' },
    { value: 'cost', label: 'Coûts' },
    { value: 'document', label: 'Documents' },
    { value: 'relance', label: 'Relances' },
    { value: 'litige', label: 'Litiges' },
    { value: 'reserve', label: 'Réserves' },
    { value: 'pv_reception', label: 'PV Réception' },
    { value: 'user', label: 'Utilisateurs' },
    { value: 'settings', label: 'Paramètres' },
];

export const AuditLogTab = () => {
    const [rows, setRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [dossiers, setDossiers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        entity_type: 'all',
        action: 'all',
        user_id: '',
        scope_kind: 'all',     // 'all' | 'client' | 'projet'
        scope_id: '',          // prospect_id or dossier_id
    });

    // Initial fetch of dropdown data
    useEffect(() => {
        (async () => {
            try {
                const [u, p, d] = await Promise.all([
                    getUsers().catch(() => ({ data: [] })),
                    getProspects().catch(() => ({ data: [] })),
                    getDossiers().catch(() => ({ data: [] })),
                ]);
                setUsers(u.data || []);
                setProspects(p.data || []);
                setDossiers(d.data || []);
            } catch (err) {
                console.warn('AuditLogTab: failed to load filter dropdowns:', err?.response?.status || err?.message);
            }
        })();
    }, []);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.entity_type !== 'all') params.entity_type = filters.entity_type;
            if (filters.action !== 'all') params.action = filters.action;
            if (filters.user_id) params.user_id = filters.user_id;
            if (filters.scope_kind === 'client' && filters.scope_id) {
                params.prospect_id = filters.scope_id;
            } else if (filters.scope_kind === 'projet' && filters.scope_id) {
                params.dossier_id = filters.scope_id;
            }
            const res = await getAuditLog(params);
            setRows(res.data || []);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [filters]);

    const userOptions = useMemo(() => ([
        { value: '', label: 'Tous les utilisateurs' },
        ...users.map((u) => ({
            value: u.id,
            label: u.name,
            sublabel: u.email,
            badge: u.role,
            keywords: `${u.email || ''} ${u.role || ''}`,
        })),
    ]), [users]);

    const scopeOptions = useMemo(() => {
        if (filters.scope_kind === 'client') {
            return prospects.map((p) => ({
                value: p.id,
                label: `${p.prenom || ''} ${p.nom || ''}`.trim() || '—',
                sublabel: [p.societe, p.ville, p.email, p.telephone].filter(Boolean).join(' · '),
                keywords: `${p.email || ''} ${p.telephone || ''} ${p.societe || ''} ${p.ville || ''}`,
            }));
        }
        if (filters.scope_kind === 'projet') {
            return dossiers.map((d) => {
                const p = prospects.find((pr) => pr.id === d.prospect_id);
                const clientName = p ? `${p.prenom || ''} ${p.nom || ''}`.trim() : '';
                return {
                    value: d.id,
                    label: d.titre || 'Sans titre',
                    sublabel: clientName ? `Client : ${clientName}` : 'Sans client',
                    keywords: `${clientName} ${p?.email || ''} ${p?.telephone || ''} ${d.type_travaux || ''}`,
                };
            });
        }
        return [];
    }, [filters.scope_kind, prospects, dossiers]);

    const resetFilters = () => setFilters({ entity_type: 'all', action: 'all', user_id: '', scope_kind: 'all', scope_id: '' });

    const activeFilterCount = (
        (filters.entity_type !== 'all' ? 1 : 0) +
        (filters.action !== 'all' ? 1 : 0) +
        (filters.user_id ? 1 : 0) +
        (filters.scope_id ? 1 : 0)
    );

    return (
        <div className="bg-white border border-[#E4E4E7] rounded-sm" data-testid="audit-log-tab">
            <div className="p-4 border-b border-[#E4E4E7] space-y-3">
                {/* Row 1: action / user / entity type */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-[#52525B] mb-1">Type d'entité</label>
                        <Select value={filters.entity_type} onValueChange={(v) => setFilters((p) => ({ ...p, entity_type: v }))}>
                            <SelectTrigger className="h-10 rounded-sm" data-testid="audit-entity-type-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {ENTITY_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#52525B] mb-1">Action</label>
                        <Select value={filters.action} onValueChange={(v) => setFilters((p) => ({ ...p, action: v }))}>
                            <SelectTrigger className="h-10 rounded-sm" data-testid="audit-action-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Toutes les actions</SelectItem>
                                <SelectItem value="create">Création</SelectItem>
                                <SelectItem value="update">Modification</SelectItem>
                                <SelectItem value="delete">Suppression</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-[#52525B] mb-1">Utilisateur</label>
                        <SearchableSelect
                            options={userOptions}
                            value={filters.user_id}
                            onChange={(v) => setFilters((p) => ({ ...p, user_id: v }))}
                            placeholder="Tous les utilisateurs"
                            emptyMsg="Aucun utilisateur"
                            allowClear={true}
                            testId="audit-user-filter"
                        />
                    </div>
                </div>

                {/* Row 2: scope by client/project */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-xs font-medium text-[#52525B] mb-1">Périmètre</label>
                        <Select
                            value={filters.scope_kind}
                            onValueChange={(v) => setFilters((p) => ({ ...p, scope_kind: v, scope_id: '' }))}
                        >
                            <SelectTrigger className="h-10 rounded-sm" data-testid="audit-scope-kind-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tout (pas de filtre)</SelectItem>
                                <SelectItem value="client">Par client</SelectItem>
                                <SelectItem value="projet">Par projet</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-[#52525B] mb-1">
                            {filters.scope_kind === 'client' ? 'Client' : filters.scope_kind === 'projet' ? 'Projet' : ''}
                        </label>
                        {filters.scope_kind === 'all' ? (
                            <div className="h-10 flex items-center text-xs text-[#52525B] italic">
                                Sélectionnez « Par client » ou « Par projet » pour cibler une entité.
                            </div>
                        ) : (
                            <SearchableSelect
                                options={scopeOptions}
                                value={filters.scope_id}
                                onChange={(v) => setFilters((p) => ({ ...p, scope_id: v }))}
                                placeholder={
                                    filters.scope_kind === 'client'
                                        ? 'Rechercher un client (nom, société, téléphone, email...)'
                                        : 'Rechercher un projet (titre, client, type travaux...)'
                                }
                                emptyMsg={filters.scope_kind === 'client' ? 'Aucun client' : 'Aucun projet'}
                                allowClear={true}
                                testId="audit-scope-id-filter"
                            />
                        )}
                    </div>
                </div>

                {/* Row 3: actions */}
                <div className="flex items-center gap-2 pt-1">
                    <Button variant="outline" onClick={load} className="rounded-sm h-9" data-testid="audit-refresh-btn">
                        <RefreshCcw className="w-4 h-4 mr-1" /> Rafraîchir
                    </Button>
                    {activeFilterCount > 0 && (
                        <Button variant="ghost" onClick={resetFilters} className="rounded-sm h-9 text-[#52525B]" data-testid="audit-reset-filters-btn">
                            <X className="w-4 h-4 mr-1" /> Réinitialiser ({activeFilterCount})
                        </Button>
                    )}
                    <span className="ml-auto text-xs text-[#52525B]">
                        {loading ? 'Chargement…' : `${rows.length} entrée${rows.length > 1 ? 's' : ''}`}
                    </span>
                </div>
            </div>

            {loading ? (
                <div className="p-8 text-center"><div className="w-6 h-6 border-4 border-[#E85A24] border-t-transparent rounded-full animate-spin mx-auto" /></div>
            ) : rows.length === 0 ? (
                <div className="p-8 text-center text-[#52525B] italic">Aucune entrée correspondant aux filtres</div>
            ) : (
                <div className="divide-y divide-[#E4E4E7] max-h-[600px] overflow-y-auto">
                    {rows.map((r) => {
                        const Icon = ICON_MAP[r.action] || Eye;
                        const color = COLOR_MAP[r.action] || 'text-gray-600 bg-gray-50';
                        const entityLabel = ENTITY_LABELS[r.entity_type] || r.entity_type;
                        // Try to resolve scope (client / project) name for context
                        const dossier = r.dossier_id ? dossiers.find((d) => d.id === r.dossier_id) : null;
                        const prospect = r.prospect_id ? prospects.find((p) => p.id === r.prospect_id) : null;
                        return (
                            <div key={r.id} className="p-3 flex items-start gap-3 text-sm" data-testid={`audit-row-${r.id}`}>
                                <div className={`p-2 rounded-sm shrink-0 ${color}`}>
                                    <Icon className="w-4 h-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p>
                                        <span className="font-semibold capitalize">{r.action}</span>
                                        <span className="text-[#52525B]"> · {entityLabel}</span>
                                        {r.entity_label && <span className="font-medium"> — {r.entity_label}</span>}
                                    </p>
                                    {(prospect || dossier) && (
                                        <p className="text-xs text-[#0A0A0A] mt-0.5">
                                            {prospect && (
                                                <span>
                                                    Client :{' '}
                                                    <b>{`${prospect.prenom || ''} ${prospect.nom || ''}`.trim() || '—'}</b>
                                                    {prospect.societe ? ` (${prospect.societe})` : ''}
                                                </span>
                                            )}
                                            {prospect && dossier && <span> · </span>}
                                            {dossier && <span>Projet : <b>{dossier.titre || '—'}</b></span>}
                                        </p>
                                    )}
                                    <p className="text-xs text-[#52525B] mt-0.5">
                                        Par <b>{r.user_name || 'inconnu'}</b> · {r.created_at ? format(new Date(r.created_at), 'dd MMM yyyy à HH:mm', { locale: fr }) : '—'}
                                    </p>
                                    {r.diff && Object.keys(r.diff).length > 0 && (
                                        <details className="mt-1">
                                            <summary className="text-xs text-[#52525B] cursor-pointer hover:text-[#0A0A0A]">Détails</summary>
                                            <pre className="text-[11px] text-[#52525B] bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm p-2 mt-1 overflow-x-auto">
                                                {JSON.stringify(r.diff, null, 2)}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
